import { Meta } from './Meta';
import type { Variants } from 'framer-motion';
import { HeaderFeature } from '../Header';
import { FooterFeature } from '../Footer';
import { PageContentResponse } from 'application/repositories/pageContentRepository';
import { PropsWithChildren, useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import { useContactLinkQuery } from 'application/hooks/useContactLinkQuery';

export const LayoutFeature: React.FC<PropsWithChildren<PageContentResponse>> = ({
	children,
	page,
	culture,
	footer,
	contactLink,
	some,
	accessToken,
	root,
	metaData,
	...props
}) => {
	const router = useRouter();

	function getBasketLink() {
		if (root?.properties?.siteSettings) {
			for (let i = 0; i < root.properties.siteSettings.length; i++) {
				const siteSetting = root.properties.siteSettings[i];
				if (siteSetting?.content?.properties['link']?.url) {
					return siteSetting.content.properties['link'].url;
				}
			}
		}
	}
	type LS = {
		languageSelector?: {
			pages?: Array<{ properties?: { continents?: Umbraco.Continent[] } }>;
		};
	};
	const pages = (props?.languageSelector as LS)?.languageSelector?.pages ?? [];
	const headerContinents: Umbraco.Continent[] = Array.isArray(pages)
		? pages.reduce<Umbraco.Continent[]>((acc, p) => {
				const conts = p?.properties?.continents;
				if (Array.isArray(conts)) acc.push(...conts);
				return acc;
		  }, [])
		: [];

	const { queryParam } = useContactLinkQuery(contactLink);

	// Use useState + useEffect to prevent hydration mismatch
	// Server renders with empty URL, client updates after hydration
	const [transformedContactLink, setTransformedContactLink] = useState({
		...contactLink?.contactLink,
		url: '',
		target: '_self' as const,
		linkType: 'internal' as const,
	});

	useEffect(() => {
		const currentUrl = window.location.pathname;
		setTransformedContactLink({
			...contactLink?.contactLink,
			url: `${currentUrl}?${queryParam}`,
			target: '_self',
			linkType: 'internal',
		});
	}, [queryParam, contactLink]);

	// Page transition animation
	const industrialSlideUp: Variants = {
		initial: { opacity: 0, y: 12 },
		enter: {
			opacity: 1,
			y: 0,
			transition: { duration: 0.28, ease: [0.22, 1, 0.36, 1], delay: 0.04 },
		},
		exit: {
			opacity: 0,
			y: -8,
			transition: { duration: 0.2, ease: [0.4, 0, 1, 1] },
		},
	};

	return (
		<>
			<Meta
				{...page?.properties}
				title={metaData?.title || page?.properties?.title}
				description={metaData?.description || page?.properties?.description}
				customPageUrls={metaData?.customPageUrls}
			/>
			<HeaderFeature
				continents={headerContinents}
				culture={culture}
				accessToken={accessToken}
				basketUrl={getBasketLink()}
				contactLink={transformedContactLink}
			/>

			{children}

			<FooterFeature
				culture={culture}
				pageFooter={footer?.pageFooter}
				contactLink={transformedContactLink}
				languages={props?.languageSelector}
				locations={props?.locations}
				socialLinks={some?.socialLinks}
			/>
		</>
	);
};
